
import { defineComponent, nextTick, reactive, ref, watch, PropType, onUnmounted } from 'vue'
import { GroupService } from './group.service'
import ContentAdaptLayout from '../content-adapt-layout/content-adapt-layout.vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { Team } from '@/services/team'
import { GroupRefreshManage } from '@/services/group'
import { ResizeService } from '@/services/resize.service'
import { Group } from '@/views/dashboard/_type/group'
import { IS_PRIVATE } from '@/common/const/env'
import { flattenArray, getTreeNode } from '@/utils/tool'
import { useRouter } from 'vue-router'
import ImportGroupDialog from './_components/import-group-dialog.vue'
import { calculateSubnetInfo } from '@/utils'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'

export default defineComponent({
  name: 'BaseTree',
  components: {
    ContentAdaptLayout,
    ImportGroupDialog
  },
  props: {
    showHeader: {
      type: Boolean,
      default: true
    },
    isBorder: {
      type: Boolean,
      default: true
    },
    width: {
      type: String,
      default: IS_JP_ONLY_BUILD ? '300px' : '260px'
    },
    subnet_address: {
      type: String,
      default: ''
    },
    treeStyle: {
      type: Object,
      default: () => ({})
    },
    showDot: {
      type: Boolean,
      default: false
    },
    /** 默认选中的分组id，传null时选择第一个可选分组 */
    defaultGroupId: {
      type: [Number, null],
      default: -1
    },
    needEdit: {
      type: Boolean,
      default: false
    },
    filterEmptyGroup: {
      type: Boolean,
      default: false
    },
    terminalType: {
      type: Array as PropType<number[]>,
      default: () => ([] as number[])
    },
    dataApi: {
      type: String,
      default: '/terminal-group/team-list'
    },
    customApiParams: {
      type: Object as PropType<any>,
      default: () => ({})
    },
    foldable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    formDisabled: {
      type: Boolean,
      default: false
    },
    toggleFold: {
      type: Boolean,
      default: true
    },
    /** 总数字段名 */
    countKey: {
      type: String,
      default: 'total'
    },
    disabledIds: {
      type: Array as PropType<number[]>,
      default: () => ([])
    }
  },
  emits: ['terminalGroupSelected', 'editTerminalGroup', 'openAddModal', 'delModal', 'update:toggleFold'],
  setup(props, context) {
    const injector = useReflectiveInjector([GroupService,])
    const resizeService = injector.get(ResizeService)
    const group = injector.get(GroupService)
    const team = injector.get(Team)
    const groupRefreshManage = injector.get(GroupRefreshManage)
    const groups = reactive<Group[]>([])
    const baseTree = ref<any>(null)
    const selectedGroup = reactive<Partial<Group>>({})
    const currentTeamId = ref<number>(null)
    const isFold = ref(false)
    const router = useRouter()
    const importDialogVisible = ref(false)
    function updateSelectedGroup(group: Group, pervSelectedId?: number) {
      if (pervSelectedId === undefined) {
        Object.assign(selectedGroup, group)
        nextTick(() => { baseTree.value?.setCurrentKey(group.group_id) })
        context.emit('terminalGroupSelected', group)
      } else {
        nextTick(() => { baseTree.value?.setCurrentKey(pervSelectedId) })
      }
    }
    function updateClickSelectedGroup(group: Group) {
      if (props.disabledIds.includes(group.group_id)) return
      Object.assign(selectedGroup, group)
      context.emit('terminalGroupSelected', group)
    }

    function isNodeDisabled(data: Group) {
      return props.disabledIds.includes(data.group_id)
    }
    team.currentTeam.subscribe(info => {
      currentTeamId.value = info.team_id
    })
    function filterGroup(grps: Group[]): Group[] {
      return grps.filter((item: Group) => {
        if (item[props.countKey]) {
          item.children = filterGroup(item.children)
          return true
        }
        return false
      })
    }
    function getFilterTerminal(keepSelected: boolean, systemType: number[]) {
      const pervSelectedId = baseTree.value?.getCurrentKey()
      const info = calculateSubnetInfo(props.subnet_address || '')
      group.getGroupList({
        team_id: currentTeamId.value,
        system_types: systemType,
        subnet_address_start: props.subnet_address ? info.fullRangeStart : undefined,
        subnet_address_end: props.subnet_address ? info.fullRangeEnd : undefined,
        ...props.customApiParams
      }, props.dataApi).then(response => {
        if (props.filterEmptyGroup) {
          response.group.children = filterGroup(response.group.children)
        }
        groups.length = 0
        groups.push(response.group)
        const initGroup = getInitGroup()
        keepSelected ? updateSelectedGroup(initGroup, pervSelectedId) : updateSelectedGroup(initGroup)
      })
    }
    function fetchGroup(keepSelected?: boolean) {
      if (props.disabled && !props.formDisabled) return

      if (props.terminalType.length) {
        getFilterTerminal(keepSelected, props.terminalType)
      } else {
        getFilterTerminal(keepSelected, IS_PRIVATE ? [1, 2, 3] : [])
      }
    }

    function getInitGroup() {
      const firstSelectableGroup = flattenArray(groups)
        .filter((group) => !props.disabledIds.includes(group.group_id))?.[0]
      const defaultGroup = getTreeNode({ tree: groups[0], field: 'group_id', childrenField: 'children', value: props.defaultGroupId })
      return props.defaultGroupId === null ? firstSelectableGroup : defaultGroup
    }

    function toggleFold() {
      isFold.value = !isFold.value
      context.emit('update:toggleFold', isFold.value)
      resizeService.resize()
    }

    const groupRefreshManageSubscribe = groupRefreshManage.onRefresh.subscribe(() => {
      fetchGroup(true)
    })
    fetchGroup()
    watch(() => props.terminalType, (newV: number[], oldV: number[]) => {
      if (newV.toString() !== oldV.toString()) {
        getFilterTerminal(false, newV)
      }
    })
    watch(() => props.customApiParams, (newV, oldV) => {
      if (JSON.stringify(newV) !== JSON.stringify(oldV)) {
        fetchGroup()
      }
    })
    watch(() => isFold.value, (nv) => {
      context.emit('update:toggleFold', nv)
    }, {
      immediate: true,
      deep: true
    })
    onUnmounted(() => {
      groupRefreshManageSubscribe.unsubscribe()
    })

    return {
      selectedGroup,
      groups,
      baseTree,
      isFold,
      IS_PRIVATE,
      importDialogVisible,
      toggleFold,
      isNodeDisabled,
      editGroup() {
        context.emit('editTerminalGroup')
      },
      addGroup() {
        context.emit('openAddModal')
      },
      importGroup() {
        importDialogVisible.value = true
      },
      handleImportSuccess() {
        fetchGroup(true)
      },
      ldapGroup() {
        router.push('/dashboard/ldap-sync-setting')
      },
      async delGroup() {
        context.emit('delModal')
      },
      clickNode(data: Group) {
        if (props.disabledIds.includes(data.group_id)) {
          // 恢复之前选中的节点，保留 is-current
          nextTick(() => { baseTree.value?.setCurrentKey(selectedGroup.group_id) })
          return
        }
        updateClickSelectedGroup(data)
      }
    }
  }
})
